import { useEffect, useRef, useState } from 'react';

import {
  MAX_PST_MESSAGE_COUNT,
  MAX_UPLOAD_ZIP_ENTRY_COUNT,
} from '../../../../../../../../../../backend/src/constants/upload-limits';
import { UploadFailureReason } from '../../../../../../../../../../backend/src/types/types';
import { FAILURE_REASON_CONFIG } from '../../utils';
import { PermittedFileTypesModal } from '@/new/components/file-uploader/permitted-file-types-modal';
import { Icon } from '@/new/components/icon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';

interface SkippedFilesExplainerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the explainer scrolls to and highlights this reason on open. */
  highlightReason?: UploadFailureReason | null;
}

interface ErrorExplanation {
  description: React.ReactNode;
  resolution?: string;
}

type RelativityFailureReason =
  | UploadFailureReason.NoFileAvailable
  | UploadFailureReason.DownloadFailed
  | UploadFailureReason.UploadError;

type GeneralFailureReason = Exclude<UploadFailureReason, RelativityFailureReason>;

export function SkippedFilesExplainerModal({ open, onOpenChange, highlightReason }: SkippedFilesExplainerModalProps) {
  const [fileTypesModalOpen, setFileTypesModalOpen] = useState(false);
  const reasonRefs = useRef(new Map<UploadFailureReason, HTMLDivElement | null>());

  useEffect(() => {
    if (!open || !highlightReason) return;
    // Defer past the dialog's open animation and Radix's autofocus — the latter scrolls
    // the content to the focused element, which would otherwise clobber our scroll.
    let removeRingTimer: ReturnType<typeof setTimeout>;
    const timer = setTimeout(() => {
      const el = reasonRefs.current.get(highlightReason);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-teal-400');
      removeRingTimer = setTimeout(() => el.classList.remove('ring-2', 'ring-teal-400'), 1500);
    }, 50);
    return () => {
      clearTimeout(timer);
      clearTimeout(removeRingTimer);
    };
  }, [open, highlightReason]);

  const GENERAL_ERROR_EXPLANATIONS: Record<GeneralFailureReason, ErrorExplanation> = {
    [UploadFailureReason.Duplicate]: {
      description: (
        <div>
          <p>
            {' '}
            This file either exists in the Matter already or it exists within the batch of documents being uploaded.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Wexler deduplicates identical files (identical files are identified by creating a hash of the file content,
            so only documents which are absolutely identical will be flagged as duplicates).
          </p>
        </div>
      ),
      resolution:
        'No action needed - the original file is already in the system or within the batch of documents being uploaded.',
    },
    [UploadFailureReason.UnsupportedFileFormat]: {
      description: (
        <p>
          The file type is not supported by Wexler.{' '}
          <button
            onClick={() => setFileTypesModalOpen(true)}
            className="text-blue-600 hover:text-blue-700 hover:underline"
          >
            View all supported file types
          </button>
        </p>
      ),
      resolution: 'Convert the file to a supported format and re-upload.',
    },
    [UploadFailureReason.EncryptedFile]: {
      description: <p>The file is password-protected. Wexler cannot open files that require a password.</p>,
      resolution: 'Remove the password from the file, then re-upload.',
    },
    [UploadFailureReason.InvalidArchive]: {
      description: (
        <p>
          The archive file (ZIP or PST) is corrupt, incomplete, or not a valid archive, so its contents could not be
          read.
        </p>
      ),
      resolution:
        'Re-create or re-download the file, then upload it again. If the issue persists, manually inspect the file or contact support.',
    },
    [UploadFailureReason.UnsafeFilePath]: {
      description: (
        <p>The file's path inside the ZIP contained an unsafe file path, so it was skipped for security reasons.</p>
      ),
      resolution: 'Re-create the ZIP without the offending paths, then upload it again.',
    },
    [UploadFailureReason.FileTooLarge]: {
      description: <p>The file exceeds the maximum supported size of 3GB, so it could not be processed.</p>,
      resolution: 'Please contact support@wexler.ai if you require large file support.',
    },
    [UploadFailureReason.TooManyEntries]: {
      description: (
        <p>
          The archive holds more items than Wexler unpacks in one go: {MAX_UPLOAD_ZIP_ENTRY_COUNT.toLocaleString()}{' '}
          entries for a ZIP and {MAX_PST_MESSAGE_COUNT.toLocaleString()} items for a PST.
        </p>
      ),
      resolution: 'Split the archive into smaller ones and upload them separately.',
    },
    [UploadFailureReason.PdfConversionFailed]: {
      description: (
        <p>
          The file was uploaded successfully, but it could not be converted into the PDF used for review. This usually
          means the file is corrupt or uses an unusual variant of its format. For audio and video, it means a
          transcription that could not be produced.
        </p>
      ),
      resolution:
        'Open the file, re-save or export it as a PDF, then upload that PDF. If the issue persists, contact support.',
    },
    [UploadFailureReason.EmptyFile]: {
      description: (
        <p>
          The file contained no data (0 bytes), so there was nothing to process. This often happens when files were
          online-only cloud placeholders when they were copied or zipped.
        </p>
      ),
      resolution: 'Make sure the files are fully downloaded to your computer, then upload them again.',
    },
    [UploadFailureReason.StorageError]: {
      description: <p> An error occurred while storing the file. This is typically a temporary system issue.</p>,
      resolution: 'Try uploading the file again. If the issue persists, contact support.',
    },
  };

  const RELATIVITY_ERROR_EXPLANATIONS: Record<RelativityFailureReason, ErrorExplanation> = {
    [UploadFailureReason.NoFileAvailable]: {
      description: (
        <p>When importing from Relativity, the file could not be found or accessed in the source workspace.</p>
      ),
      resolution: 'Verify the file exists in Relativity and that the integration has the correct permissions.',
    },
    [UploadFailureReason.DownloadFailed]: {
      description: (
        <p>
          The file could not be downloaded from Relativity. This may be due to network issues or temporary
          unavailability.
        </p>
      ),
      resolution:
        'Try the import again. If the issue persists, check your connection to the source system and contact support.',
    },
    [UploadFailureReason.UploadError]: {
      description: <p>The file failed to upload. This may be due to a network issue or the file being too large.</p>,
      resolution:
        'Check your network connection and try uploading the file again. If the issue persists, contact support.',
    },
  };

  const renderExplanations = (explanations: Partial<Record<UploadFailureReason, ErrorExplanation>>) =>
    (Object.entries(explanations) as [UploadFailureReason, ErrorExplanation][]).map(
      ([reason, { description, resolution }]) => {
        const config = FAILURE_REASON_CONFIG[reason];
        return (
          <div
            key={reason}
            ref={(el) => reasonRefs.current.set(reason, el)}
            className="rounded-md border border-gray-100 bg-gray-50/50 p-3 transition-shadow duration-500"
          >
            <div className="mb-2 flex items-center gap-2">
              <div className="flex items-center justify-center rounded p-1">
                <Icon className={`${config.icon} h-3.5 w-3.5`} />
              </div>
              <h4 className="font-medium text-gray-800">{config.label}</h4>
            </div>
            <p className="text-gray-600">{description}</p>
            {resolution && (
              <p className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Resolution:</span> {resolution}
              </p>
            )}
          </div>
        );
      },
    );

  return (
    <>
      <PermittedFileTypesModal open={fileTypesModalOpen} onOpenChange={setFileTypesModalOpen} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Why are files skipped?</DialogTitle>
            <DialogDescription>
              Files may be skipped during upload for various reasons. Here's what each status means.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {/* General Errors */}
            {renderExplanations(GENERAL_ERROR_EXPLANATIONS)}

            <div className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs font-medium text-gray-400">Relativity Errors</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Relativity Errors */}
            {renderExplanations(RELATIVITY_ERROR_EXPLANATIONS)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
